import { AxiosError } from "axios";
import { Loader2 } from "lucide-react";
import { useCallback, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { useCampaign } from "@/features/campaigns/api/useCampaign";
import { ArchivedSegmentBanner } from "@/features/campaigns/components/ArchivedSegmentBanner";
import { useAddCampaignJobBoardMutation } from "@/features/campaigns/jobboards/api/useAddCampaignJobBoardMutation";
import AddCampaignJobBoardForm from "@/features/campaigns/jobboards/components/CampaignJobBoardForm/AddCampaignJobBoardForm";
import {
  getCampaignDetailPath,
  getCampaignsPath,
} from "@/features/campaigns/utils/navigation";
import type { ProblemDetails } from "@/lib/helpers/handleProblemDetailsError";
import { useBreadcrumbs } from "@/lib/hooks/useBreadcrumbs";
import useToast from "@/lib/hooks/useToast";
import { AddCampaignJobBoardRequest } from "@/types/request/AddCampaignJobBoardRequest";
import { ValidationErrorResponse } from "@/types/response/ValidationResponse";

const SEGMENT_ARCHIVED_ERROR = "Campaign.SegmentArchived";

type AddCampaignJobBoardPageContentProps = {
  campaignId: string;
  clientId?: string;
};

function AddCampaignJobBoardPageContent({
  campaignId,
  clientId,
}: AddCampaignJobBoardPageContentProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const campaignDetailPath = getCampaignDetailPath(clientId, campaignId);

  const handleNavigateBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const { data: campaign, isLoading: isCampaignLoading } = useCampaign({
    campaignId,
    clientId: clientId || undefined,
  });

  // Normalize clientId to empty string (matching CampaignDetailPage behavior)
  // This ensures query keys match correctly for optimistic updates
  const normalizedClientId = clientId ?? "";

  const addCampaignJobBoard = useAddCampaignJobBoardMutation({
    campaignId,
    clientId: normalizedClientId,
  });

  const handleSubmit = useCallback(
    (payload: AddCampaignJobBoardRequest) => {
      setErrorMessage(null);
      addCampaignJobBoard.mutate(payload, {
        onSuccess: () => {
          showToast(t("success.added", { entity: t("common.jobBoard") }));
          // Always navigate to campaign overview - optimistic update handles showing the new CJB
          navigate(campaignDetailPath);
        },
        onError: (
          error: AxiosError<ValidationErrorResponse | ProblemDetails>,
        ) => {
          const data = error.response?.data;
          const errorMessages = Array.isArray(data)
            ? data.map(({ errorMessage }) => t(`validation.${errorMessage}`))
            : [t("errors.somethingWentWrong")];
          const isSegmentArchived =
            !Array.isArray(data) && data?.title === SEGMENT_ARCHIVED_ERROR;

          const message = isSegmentArchived
            ? t("campaigns.segmentArchived.title")
            : errorMessages[0];
          setErrorMessage(message);
          showToast(message, "error");
        },
      });
    },
    [navigate, addCampaignJobBoard, campaignDetailPath, showToast, t],
  );

  const breadcrumbs = useBreadcrumbs({
    items: [
      { label: t("common.campaigns"), href: getCampaignsPath(clientId) },
      {
        label: campaign?.name ?? t("common.campaign"),
        href: campaignDetailPath,
      },
      { label: t("common.jobBoards") },
      { label: t("common.add") },
    ],
  });

  let content: ReactNode;

  if (isCampaignLoading) {
    content = (
      <div className="flex h-full w-full items-center justify-center p-6">
        <Loader2 className="size-16 animate-spin text-muted-foreground" />
      </div>
    );
  } else if (campaign?.segment?.archivedAt != null) {
    content = (
      <ArchivedSegmentBanner
        clientId={normalizedClientId}
        campaignId={campaignId}
        segmentName={campaign.segment.name}
      />
    );
  } else if (campaign) {
    content = (
      <AddCampaignJobBoardForm
        campaignId={campaignId}
        clientId={clientId}
        errorMessage={errorMessage}
        isSubmitting={addCampaignJobBoard.isPending}
        onSubmitForm={handleSubmit}
        onSuccess={handleNavigateBack}
      />
    );
  }

  return (
    <ModernPageLayout
      breadcrumbs={breadcrumbs}
      backbutton={{
        href: "../../",
        label: t("common.back"),
      }}
      title={t("campaignJobBoard.pages.add.title")}
      subtitle={t("campaignJobBoard.pages.add.subtitle", {
        campaignName: campaign?.name,
      })}
    >
      {content}
    </ModernPageLayout>
  );
}

function AddCampaignJobBoardPage() {
  const { campaignId, clientId } = useParams();

  if (!campaignId) {
    return null;
  }

  return (
    <AddCampaignJobBoardPageContent
      campaignId={campaignId}
      clientId={clientId}
    />
  );
}

export default AddCampaignJobBoardPage;
